/**
 * CommandCodec — registry that maps serialized command `type` tags back to
 * live UndoCommand instances.
 *
 * Each command module registers its own deserializer at import time (a
 * side-effect export). This keeps codec logic colocated with the command
 * class rather than forcing a central switch statement that has to know
 * about every command type.
 *
 * Deserialization takes a `TraceScene` because path-keyed refs (FQTPs
 * produced by `sceneObject.getPath()`) must be resolved against the
 * current scene graph before the command can run.
 */

import type { UndoCommand, SerializedCommand } from './UndoCommand';
import type { TraceScene } from '../../core/TraceScene';

export type CommandDeserializer = (data: unknown, scene: TraceScene) => UndoCommand;

const codecs = new Map<string, CommandDeserializer>();

/** Register a deserializer for a command type tag. Idempotent (last call wins). */
export function registerCommandCodec(type: string, deserialize: CommandDeserializer): void {
    codecs.set(type, deserialize);
}

/**
 * Rebuild a live command from its serialized form.
 * Throws if no codec is registered for the given type — replay callers
 * should catch this and either skip the event or halt, per their policy.
 */
export function deserializeCommand(serialized: SerializedCommand, scene: TraceScene): UndoCommand {
    const codec = codecs.get(serialized.type);
    if (!codec) {
        throw new Error(`[CommandCodec] No deserializer registered for "${serialized.type}"`);
    }
    return codec(serialized.data, scene);
}

/** For tests: inspect the registry. */
export function hasCommandCodec(type: string): boolean {
    return codecs.has(type);
}
